import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import RowContainer from './rowContainer';
import clsx from 'clsx';
import DictParser from '../../../../util/dictParser';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getConfig } from '../../../../util/configUtil';
import { getAdmissionStartDate } from '../../../../util/patientUtil';
import _ from 'lodash';
import Chip from '@mui/material/Chip';
import config from '../../../../app/config';
import { useTheme } from '@mui/material/styles';
import { useTimelineStyles } from '../useTimelineStyles';
import { getAcuityPriority, getTriageButtonColorMatrix } from '../../../../pages/ward/forms/triage/triageUtils';

const EdTimelineRow = ({ admission }) => {
    const classes = useTimelineStyles();
    const { t } = useTranslation();
    const theme = useTheme();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');
    const isReqTreatInvestEnabled = getConfig('requestedTreatmentAndInvestigation', false);

    const getDiagnosis = () => {
        const renderDiagnosisChip = () => {
            if (!_.isEmpty(admission?.diagnosis?.primary)) {
                return admission?.diagnosis?.primary?.map((diag) => (
                    <Chip
                        key={diag?.id}
                        size="small"
                        label={diag?.value?.snomedDescription || diag?.value?.__description}
                        style={{ background: checkQualifier(diag, 'chip'), marginRight: '10px' }}
                        avatar={
                            <div
                                className={classes.avatarCustom}
                                style={{
                                    lineHeight: '20px',
                                    color: '#fff',
                                    background: checkQualifier(diag, 'avatar'),
                                }}
                            >
                                {diag?.sequenceNumber}
                            </div>
                        }
                    />
                ));
            }
            return t('Not set');
        };

        return (
            <div className={classes.divRow} style={{ width: '100%' }}>
                <div style={{ width: '20%' }}>{t('Diagnosis')}</div>
                <div style={{ width: '80%', display: 'flex', gap: 5, flexWrap: 'wrap' }}>{renderDiagnosisChip()}</div>
            </div>
        );
    };

    const getSpecialtyReferral = () => {
        const descriptions = _.chain(admission?.studies?.referrals?.ReferralsToOtherServices)
            .map('ReferredToService_SnomedCt.__description')
            .join(', ')
            .value();

        return (
            <div className={classes.divRow} style={{ width: '100%' }}>
                <div style={{ width: '20%' }}>{t('Speciality Referral')}</div>
                <div style={{ width: '80%' }}>{_.isEmpty(descriptions) ? t('Not set') : descriptions}</div>
            </div>
        );
    };

    const getClinician = () => {
        const getCurrentClinician = () => {
            if (admission?.currentClinician) {
                const current = admission?.currentClinician;
                if (_.isString(current)) return current;
                else if (_.isObject(current) && current?.__description) return current?.__description;
            }
            return t('Not set');
        };

        return (
            <div className={classes.divRow} style={{ width: '100%' }}>
                <div style={{ width: '20%' }}>{t('Clinician Set')}</div>
                <div style={{ width: '80%' }}>{getCurrentClinician()}</div>
            </div>
        );
    };

    const getPriority = () => {
        const acuity = admission?.triage?.discriminator ?? admission?.initialAssessment?.discriminator;

        const getDiscriminator = () => {
            let description;
            const score = getAcuityPriority(acuity);
            switch (score) {
                case 1:
                    description = t('Immediate');
                    break;
                case 2:
                    description = t('Very Urgent');
                    break;
                case 3:
                    description = t('Urgent');
                    break;
                case 4:
                    description = t('Standard');
                    break;
                case 5:
                    description = t('Not Urgent');
                    break;
                default:
                    description = t('Not set');
                    break;
            }

            return description;
        };

        return (
            <div className={classes.divRow} style={{ width: '100%' }}>
                <div style={{ width: '20%' }}>{t('Priority')}</div>

                <div className={classes.divRow} style={{ width: '80%', gap: 5 }}>
                    {acuity && <div className={classes.discriminator} style={{ backgroundColor: getTriageButtonColorMatrix(acuity) }} />}
                    <span>{getDiscriminator()}</span>
                </div>
            </div>
        );
    };

    const getArrival = () => {
        const getCC = () => {
            if (admission?.triage?.chiefComplaint) {
                return DictParser.chiefComplaint(admission?.triage?.chiefComplaint, false);
            } else if (admission?.admission?.initialAEComplaintDescription) {
                return admission?.admission?.initialAEComplaintDescription;
            }
            return t('Not set');
        };

        return (
            <div className={classes.divRow} style={{ width: '100%' }}>
                <div style={{ width: '20%' }}>
                    <div>{t('Arrival')}</div>

                    <div className={classes.smallText}>{getAdmissionStartDate(admission?.admission)?.format(dateFormat)}</div>
                </div>

                <div style={{ width: '80%' }}>
                    <div>
                        <div className={classes.smallText}>{t('Chief Complaint')}</div>

                        <div>{getCC()}</div>
                    </div>
                </div>
            </div>
        );
    };

    const checkQualifier = (element, mode) => {
        if (isReqTreatInvestEnabled) {
            // For diagnosis check qualifier for rest check procedure date
            const qualifierSuspected = element?.qualifier && element?.qualifier?.snomedDescription === 'Suspected';
            if (mode === 'chip') {
                return qualifierSuspected
                    ? config?.diagnosisColorsSuspected?.chip || '#f7a202'
                    : config?.diagnosisColorsConfirm?.chip || '#4eac6f';
            } else {
                return qualifierSuspected
                    ? config?.diagnosisColorsSuspected?.main || '#634101'
                    : config?.diagnosisColorsConfirm?.main || '#2e6441';
            }
        } else {
            const qualifierSuspected = element?.qualifier && element?.qualifier?.snomedDescription === 'Suspected';
            const check = element.qualifier === null || element.sequenceNumber === '';
            if (check) return '';

            if (mode === 'chip') {
                return qualifierSuspected ? theme.palette.primary.dark : theme.palette.primary.main;
            } else {
                return qualifierSuspected ? theme.palette.primary.main : theme.palette.primary.dark;
            }
        }
    };

    return (
        <>
            <TimelineItem>
                <TimelineSeparator className={classes.tinySeparator}>
                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled" />
                    <TimelineConnector className={classes.connector} />
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>{getDiagnosis()}</RowContainer>
                </TimelineContent>
            </TimelineItem>

            <TimelineItem>
                <TimelineSeparator className={classes.tinySeparator}>
                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled" />
                    <TimelineConnector className={classes.connector} />
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>{getSpecialtyReferral()}</RowContainer>
                </TimelineContent>
            </TimelineItem>

            <TimelineItem>
                <TimelineSeparator className={classes.tinySeparator}>
                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled" />
                    <TimelineConnector className={classes.connector} />
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>{getClinician()}</RowContainer>
                </TimelineContent>
            </TimelineItem>

            <TimelineItem>
                <TimelineSeparator className={classes.tinySeparator}>
                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled" />
                    <TimelineConnector className={classes.connector} />
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>{getPriority()}</RowContainer>
                </TimelineContent>
            </TimelineItem>

            <TimelineItem>
                <TimelineSeparator>
                    <TimelineDot className={classes.dot} variant="outlined" />
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>{getArrival()}</RowContainer>
                </TimelineContent>
            </TimelineItem>
        </>
    );
};

export default EdTimelineRow;
